import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';

import { ApplicationState } from 'store';
import { promiseDispatch } from 'store/utils/promiseAction';
import { seatPreReserveRequest } from 'store/ducks/itineraries/actions';

import { maxSeat } from 'helpers/ticketSales';

import ENVIRONMENTS from 'constants/enviroments';

import { ItineraryListInterface } from 'store/ducks/publicStore/types';
import { ResumeItineraryVesselsInterface } from 'store/ducks/itineraries/types';

const OCUPED_SEAT_STATUS = 'seatmapservice.prereserve.held';

type SeatEntry = {
  seatNumber: number;
  value: number;
  id: string;
  floor: number;
  type: number;
};

export interface SeatSelectionResult {
  seatField: { [tripScheduleId: string]: SeatEntry[] };
  setSeatField: React.Dispatch<
    React.SetStateAction<{ [tripScheduleId: string]: SeatEntry[] }>
  >;
  ocupedSeat: { [tripScheduleId: string]: string[] };
  setOcupedSeat: React.Dispatch<
    React.SetStateAction<{ [tripScheduleId: string]: string[] }>
  >;
  floor: number;
  setFloor: (floor: number) => void;
  /**
   * Pré-reserva os assentos selecionados e chama o callback adequado.
   * @param trip - viagem selecionada
   * @param collapse - lista de tripScheduleIds com o mapa aberto
   * @param isSea - se é embarcação (pula reserva de assento)
   * @param onNextStep - callback chamado após sucesso no fluxo normal
   * @param onConfirmReschedule - callback chamado após sucesso no fluxo de reagendamento
   */
  handleChekSeat: (
    trip: ItineraryListInterface,
    collapse: string[],
    isSea: boolean,
    onNextStep: (
      trip: ItineraryListInterface,
      holdTokens: HoldToken[],
      seatType: number,
    ) => void,
    onConfirmReschedule: (
      trip: ItineraryListInterface,
      holdTokens?: HoldToken[],
      seatType?: number,
    ) => void,
  ) => Promise<void>;
  getCollapseKey: (tripScheduleId: string, seatType?: number) => string;
}

export type HoldToken = {
  holdToken: any;
  tripScheduleSeatId: string;
  value: number;
  floor: number;
  type: number;
  seatNumber: number;
  tripScheduleId: string;
};

export function useSeatSelection(
  initialItineraryResumeList: ResumeItineraryVesselsInterface | null,
): SeatSelectionResult {
  const step = useSelector((state: ApplicationState) => state.publicStore.step);

  const [seatField, setSeatField] = useState<{
    [tripScheduleId: string]: SeatEntry[];
  }>({});
  const [ocupedSeat, setOcupedSeat] = useState<{
    [tripScheduleId: string]: string[];
  }>({});
  const [floor, setFloor] = useState(1);

  // Inicializa seatField quando a lista de viagens carrega
  useEffect(() => {
    const aux: { [key: string]: SeatEntry[] } = {};
    initialItineraryResumeList?.trips?.forEach((t) => {
      aux[t.tripScheduleId] = [];
    });
    setSeatField(aux);
  }, [initialItineraryResumeList]);

  const getCollapseKey = (tripScheduleId: string, seatType?: number) =>
    seatType !== undefined ? `${tripScheduleId}-${seatType}` : tripScheduleId;

  const getSelectedSeatType = (trip: ItineraryListInterface) => {
    const seatList = (trip as any).seats || [];
    const enabledSeatType = seatList.find((seat: any) => seat.enabled)
      ?.seatType;

    if (typeof enabledSeatType === 'number') {
      return enabledSeatType;
    }

    const fallbackSeatType = maxSeat(seatList);
    return fallbackSeatType > 0 ? fallbackSeatType : undefined;
  };

  const handleChekSeat = async (
    trip: ItineraryListInterface,
    collapse: string[],
    isSea: boolean,
    onNextStep: (
      trip: ItineraryListInterface,
      holdTokens: HoldToken[],
      seatType: number,
    ) => void,
    onConfirmReschedule: (
      trip: ItineraryListInterface,
      holdTokens?: HoldToken[],
      seatType?: number,
    ) => void,
  ) => {
    const holdTokens: HoldToken[] = [];
    const isRescheduling = step.confirmRescheduling.toShow;
    const selectedSeatType = getSelectedSeatType(trip);
    const seatFieldKey = getCollapseKey(trip.tripScheduleId, selectedSeatType);

    if (!isSea) {
      if (collapse.includes(seatFieldKey)) {
        const selectedSeats = seatField?.[seatFieldKey] || [];
        const aux: { [key: string]: string[] } = {
          [seatFieldKey]: [],
        };

        for await (const { id } of selectedSeats) {
          try {
            const result = await promiseDispatch(
              seatPreReserveRequest({
                tripScheduleSeatId: id,
                transportOperatorId: ENVIRONMENTS.OPERATOR_ID,
              }),
            );

            if (result?.status === 200) {
              holdTokens.push({
                holdToken: result.data.holdToken,
                tripScheduleSeatId: id,
                value: selectedSeats.find((item) => item.id === id)?.value || 0,
                floor: selectedSeats.find((item) => item.id === id)?.floor || 0,
                type: selectedSeats.find((item) => item.id === id)?.type || 0,
                seatNumber:
                  selectedSeats.find((item) => item.id === id)?.seatNumber || 0,
                tripScheduleId: trip.tripScheduleId,
              });
            } else if (
              id &&
              result?.status === 400 &&
              result?.data?.internalCode === OCUPED_SEAT_STATUS
            ) {
              aux[seatFieldKey].push(id);
            }
          } catch (_error) {
            if (id) aux[seatFieldKey].push(id);
          }
        }

        setOcupedSeat((prev) => ({
          ...prev,
          [seatFieldKey]: aux[seatFieldKey],
        }));

        if (holdTokens.length === selectedSeats.length) {
          setFloor(1);
          if (isRescheduling) {
            onConfirmReschedule(trip, holdTokens, selectedSeatType);
          } else {
            onNextStep(trip, holdTokens, selectedSeatType || 0);
          }
        }
      }
    } else {
      if (isRescheduling) {
        onConfirmReschedule(trip, holdTokens, selectedSeatType);
      } else {
        onNextStep(trip, holdTokens, selectedSeatType || 0);
      }
    }
  };

  return {
    seatField,
    setSeatField,
    ocupedSeat,
    setOcupedSeat,
    floor,
    setFloor,
    handleChekSeat,
    getCollapseKey,
  };
}
